// comments.ts — comment feed model + view (Phase 2, docs §7).
//
// Authoring uses a FREEZE flow (docs §7, the key request): pressing the hotkey
// or the "Add comment" button immediately FREEZES the currently-heard
// transport position and opens the input with a frozen badge showing the
// anchor (the REAPER label when present, else the time). The frozen anchor is
// fixed once frozen, and the comment is POSTed with the FROZEN
// projectSec, not the live one (docs §4/§7). While paused the anchor is
// naturally fixed.
//
// New comments also arrive live over the `ctrl` data channel (room.ts) — both
// paths funnel through addOrUpdate(), deduped by id, so the local echo and the
// relayed copy never double up. Edits/deletes from the host or owner update the
// view in place (docs §7.4).

import { formatPosition, type TransportDisp } from "./timecode";
import {
  postComment,
  resolveComment,
  editComment,
  deleteComment,
  requestSeek,
  ApiError,
  type Comment,
  type CommentConfig,
} from "./api";
import { ResolutionStore } from "./comment-resolution";
import { colorForClientId } from "./client";

/** Step sizes for nudging the frozen anchor (docs §7: small steps). */

export interface CommentsDeps {
  sessionId: string;
  /** Listener/guest token (the LiveKit token from /join) for authoring. */
  getToken: () => string | undefined;
  /** Display name chosen at the join gate. */
  getAuthor: () => string;
  /** This listener's clientId (docs §7.1) — owner of authored comments. */
  getClientId: () => string;
  /** This listener's server-assigned color (docs §7.1), if known. */
  getOwnColor: () => string | undefined;
  /** The CURRENTLY DISPLAYED project time in seconds (docs §4). */
  getCurrentProjectSec: () => number;
  /** The CURRENTLY DISPLAYED REAPER label (docs §7.7), if any. */
  getCurrentLabel: () => string | undefined;
  /** The current §7.7 display context, so comments render in the same unit as the transport. */
  getCurrentDisp: () => TransportDisp | undefined;
  /** Streamer JWT when in HOST mode (docs §7.3) — enables edit/delete on all. */
  getHostToken: () => string | undefined;
  /** Surface a non-fatal error. */
  onError?: (message: string) => void;
}

/** A frozen authoring anchor (docs §7): captured once, then posted verbatim. */
interface FrozenAnchor {
  projectSec: number;
  /** Verbatim REAPER label captured at freeze time (docs §7.7), if any. */
  label?: string;
  /** Display context captured at freeze time so the badge matches the transport unit. */
  disp?: TransportDisp;
}

/**
 * Owns the comment list state and renders into a provided container.
 * Sorted by projectSec ascending so the feed reads like a timeline.
 */
export class CommentsView {
  private readonly byId = new Map<string, Comment>();
  private listEl!: HTMLUListElement;
  private inputEl!: HTMLTextAreaElement;
  private selfDot!: HTMLSpanElement;
  private formEl!: HTMLFormElement;
  private submitBtn!: HTMLButtonElement;
  private freezeBtn!: HTMLButtonElement;
  private cancelBtn!: HTMLButtonElement;
  private disabledHint!: HTMLElement;
  private rootSection!: HTMLElement;
  private posLabel!: HTMLElement;
  private keyHint!: HTMLElement;
  private resolution = new ResolutionStore();
  private filter: "open" | "all" = "all";
  private filterButtons: HTMLButtonElement[] = [];
  private resolving = new Set<string>();
  private posTimer: number | null = null;

  /** Tombstones: ids deleted live this session. setInitial()/addOrUpdate()
   *  consult them so a stale REST snapshot or late relay cannot resurrect a
   *  deleted comment (or revert a newer edit). */
  private deletedIds = new Set<string>();
  /** When non-null, the anchor is frozen and authoring is open (docs §7). */
  private frozen: FrozenAnchor | null = null;
  /** Comment id currently being edited inline (survives re-renders). */
  private editingId: string | null = null;
  /** Draft text of the inline editor, preserved across re-renders. */
  private editingDraft = "";
  /** Focus the inline editor on the NEXT render only (when it was just
   *  opened) — focusing on every re-render stole the caret whenever a live
   *  comment arrived mid-typing. */
  private focusEditorOnRender = false;
  /** Comment moderation config (docs §7.3). Defaults to enabled, non-private. */
  private config: CommentConfig = { enabled: true, privateMode: false };

  private readonly onKeyDown = (e: KeyboardEvent) => this.handleHotkey(e);

  constructor(private readonly deps: CommentsDeps) {}

  /** Build the DOM into `container` and wire the handlers. */
  mount(container: HTMLElement): void {
    container.innerHTML = "";

    const wrap = document.createElement("section");
    wrap.className = "comments";
    this.rootSection = wrap;

    const heading = document.createElement("h2");
    heading.className = "comments__heading";
    heading.textContent = "Comments";
    wrap.appendChild(heading);
    if (this.deps.getHostToken()) {
      this.filter = "open";
      const filters = document.createElement("div"); filters.className = "comments__filters";
      for (const value of ["open", "all"] as const) {
        const button = document.createElement("button"); button.type = "button"; button.textContent = value === "open" ? "Open" : "All";
        button.dataset.filter = value; button.onclick = () => { this.filter = value; this.render(); };
        this.filterButtons.push(button); filters.append(button);
      }
      wrap.append(filters);
    }

    this.listEl = document.createElement("ul");
    this.listEl.className = "comments__list";
    this.listEl.setAttribute("aria-live", "polite");

    // --- Freeze trigger row (idle state) ---------------------------------
    const triggerRow = document.createElement("div");
    triggerRow.className = "comments__trigger";

    this.posLabel = document.createElement("span");
    this.posLabel.className = "comments__pos";
    this.posLabel.title = "Currently heard project position (press \u2018c\u2019 to freeze it)";
    this.posLabel.textContent = "@ 00:00:00.000";

    this.freezeBtn = document.createElement("button");
    this.freezeBtn.type = "button";
    this.freezeBtn.className = "btn btn--primary comments__freeze";
    this.freezeBtn.textContent = "Add comment";
    this.freezeBtn.title = "Freeze the position and comment (key: Enter)";
    this.freezeBtn.addEventListener("click", () => this.freezeHere());

    triggerRow.append(this.freezeBtn, this.posLabel);
    wrap.appendChild(triggerRow);

    // Smart hint: a single Enter sets a comment at the heard position; a second
    // Enter sends it. Shown in the idle state, hidden once authoring is open.
    this.keyHint = document.createElement("p");
    this.keyHint.className = "comments__keyhint";
    this.keyHint.innerHTML = `Tip: press <kbd>↵ Enter</kbd> to comment at the current position`;
    wrap.appendChild(this.keyHint);

    // --- Authoring form (frozen state) -----------------------------------
    this.formEl = document.createElement("form");
    this.formEl.className = "comments__form";
    this.formEl.noValidate = true;
    this.formEl.hidden = true;

    // Compose row: the self-identity dot (§13 "who am I here") next to a
    // textarea that GROWS with the comment. The frozen position is already on
    // display in the trigger row's "@ …" label — no duplicate badge.
    const composeRow = document.createElement("div");
    composeRow.className = "comments__compose";

    this.selfDot = document.createElement("span");
    this.selfDot.className = "comments__selfdot";
    this.selfDot.title = "Your color";

    this.inputEl = document.createElement("textarea");
    this.inputEl.className = "comments__input";
    this.inputEl.placeholder = "Comment at this position\u2026";
    this.inputEl.maxLength = 500;
    this.inputEl.rows = 1;
    this.inputEl.autocomplete = "off";
    this.inputEl.addEventListener("input", () => this.autogrow(this.inputEl));
    // Enter sends (matching the single-line habit); Shift+Enter makes a line.
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void this.submit();
      }
    });

    composeRow.append(this.selfDot, this.inputEl);

    this.submitBtn = document.createElement("button");
    this.submitBtn.type = "submit";
    this.submitBtn.className = "comments__submit";
    this.submitBtn.textContent = "Send";

    this.cancelBtn = document.createElement("button");
    this.cancelBtn.type = "button";
    this.cancelBtn.className = "btn btn--ghost comments__cancel";
    this.cancelBtn.textContent = "Cancel";
    this.cancelBtn.addEventListener("click", () => this.unfreeze());

    const actionsRow = document.createElement("div");
    actionsRow.className = "comments__formactions";
    actionsRow.append(this.submitBtn, this.cancelBtn);

    this.formEl.append(composeRow, actionsRow);
    wrap.appendChild(this.formEl);

    // --- Disabled hint (comments off, docs §7.4) -------------------------
    this.disabledHint = document.createElement("p");
    this.disabledHint.className = "comments__hint";
    this.disabledHint.hidden = true;
    this.disabledHint.textContent = "Comments are currently disabled.";
    wrap.appendChild(this.disabledHint);

    // Author at the top; history follows the composer and its filters.
    const filters = wrap.querySelector(".comments__filters");
    if (filters) wrap.appendChild(filters);
    wrap.appendChild(this.listEl);
    container.appendChild(wrap);

    this.formEl.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.submit();
    });

    // Global hotkey: 'c' freezes the heard position (docs §7). Ignored while
    // typing into a field or when comments are disabled.
    window.addEventListener("keydown", this.onKeyDown);

    // Keep the idle "@ position" label ticking so the user sees where a freeze
    // would land. While frozen the badge is fixed (no ticking).
    this.posTimer = window.setInterval(() => {
      if (!this.frozen) this.posLabel.textContent = "@ " + this.liveAnchorText();
    }, 100);

    this.applyConfigToDom();
    this.render();
  }

  /**
   * Seed the view with the existing comment list (GET /api/.../comments).
   * In private mode the server already returns only the caller's comments
   * (docs §7.3); we also filter defensively in case the GET resolves after a
   * config change has flipped privateMode on.
   */
  setInitial(comments: Comment[]): void {
    const me = this.deps.getClientId();
    for (const c of comments) {
      if (this.config.privateMode && c.clientId && c.clientId !== me) continue;
      // The REST snapshot raced the live channel: never clobber newer state
      // (a live edit already applied, or a deletion — tombstoned above).
      this.rememberResolution(c.id, !!c.resolved, c.resolutionVersion ?? 0);
      if (this.byId.has(c.id) || this.deletedIds.has(c.id)) continue;
      this.byId.set(c.id, c);
    }
    this.render();
  }

  /** Apply/refresh the comment moderation config (docs §7.3/§7.4). */
  /** §13: personalize the input with the listener's own name + color so they
   *  instantly see who they are (names are unique per session now). */
  setSelf(name: string, color: string | undefined): void {
    if (this.inputEl) this.inputEl.placeholder = `Comment as ${name}\u2026`;
    if (this.selfDot) {
      this.selfDot.style.background = color ?? "var(--brand-accent, #c9a84c)";
      this.selfDot.title = `${name} - your color`;
    }
  }

  setConfig(config: CommentConfig): void {
    this.config = config;
    if (!config.enabled && this.frozen) this.unfreeze();
    if (config.privateMode) this.dropOthersComments();
    this.applyConfigToDom();
    this.render();
  }

  /** Insert or update a single comment (live `ctrl` packet or local echo). */
  addOrUpdate(c: Comment): void {
    // In private mode, only the caller's own comments are relevant (the server
    // also filters, but guard defensively, docs §7.4).
    if (this.config.privateMode && c.clientId && c.clientId !== this.deps.getClientId()) {
      return;
    }
    if (this.deletedIds.has(c.id)) return; // deleted live — stay deleted
    this.rememberResolution(c.id, !!c.resolved, c.resolutionVersion ?? 0);
    this.byId.set(c.id, c);
    this.render();
  }

  private rememberResolution(id: string, resolved: boolean, version: number): void {
    this.resolution.remember(id, resolved, version);
  }

  applyResolved(id: string, resolved: boolean, version: number): void {
    this.rememberResolution(id, resolved, version);
    this.render();
  }

  private async toggleResolved(c: Comment): Promise<void> {
    const token = this.deps.getHostToken();
    if (!token || this.resolving.has(c.id)) return;
    this.resolving.add(c.id); this.render();
    try {
      const result = await resolveComment(this.deps.sessionId, c.id, !this.resolution.get(c.id)?.resolved, token);
      this.applyResolved(result.id, !!result.resolved, result.resolutionVersion ?? 0);
    } catch (e) { this.deps.onError?.(`Could not save comment status: ${this.msg(e)}`); }
    finally { this.resolving.delete(c.id); this.render(); }
  }

  /** Apply a live edit (docs §7.4 comment_edit): change text, mark edited. */
  applyEdit(id: string, text: string): void {
    const existing = this.byId.get(id);
    if (!existing) return;
    this.byId.set(id, { ...existing, text, edited: true });
    this.render();
  }

  /** Apply a live delete (docs §7.4 comment_delete). */
  applyDelete(id: string): void {
    this.deletedIds.add(id);
    if (this.byId.delete(id)) this.render();
  }

  /** Apply a live color override (docs §7.4 listener_color): recolor every
   *  comment owned by `clientId`. */
  applyColor(clientId: string, color: string): void {
    let changed = false;
    for (const [id, c] of this.byId) {
      if (c.clientId === clientId && c.color !== color) {
        this.byId.set(id, { ...c, color });
        changed = true;
      }
    }
    if (changed) this.render();
  }

  // -------------------------------------------------------------------------
  // Freeze flow (docs §7)
  // -------------------------------------------------------------------------

  /** Freeze the currently-heard transport position and open the input. */
  /** Grow a textarea with its content (rows=1 baseline, no scrollbar). */
  private autogrow(el: HTMLTextAreaElement): void {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  private freezeHere(): void {
    if (!this.config.enabled) return;
    const projectSec = this.deps.getCurrentProjectSec();
    const label = this.deps.getCurrentLabel();
    const disp = this.deps.getCurrentDisp();
    const anchor: FrozenAnchor = { projectSec };
    if (label !== undefined) anchor.label = label;
    if (disp !== undefined) anchor.disp = disp;
    this.frozen = anchor;
    this.formEl.hidden = false;
    this.freezeBtn.hidden = true;
    this.keyHint.hidden = true;
    this.posLabel.classList.add("comments__pos--frozen");
    this.inputEl.focus();
  }

  /** Close the authoring form without sending. */
  private unfreeze(): void {
    this.frozen = null;
    this.inputEl.value = "";
    this.inputEl.style.height = "";
    this.formEl.hidden = true;
    this.freezeBtn.hidden = !this.config.enabled;
    this.keyHint.hidden = !this.config.enabled;
    this.posLabel.classList.remove("comments__pos--frozen");
  }

  /** Idle anchor text: formatted in the same unit as the transport (docs §7.7). */
  private liveAnchorText(): string {
    return formatPosition(
      this.deps.getCurrentProjectSec(),
      this.deps.getCurrentDisp(),
      this.deps.getCurrentLabel(),
    );
  }

  private handleHotkey(e: KeyboardEvent): void {
    if (!this.rootSection?.getClientRects().length) return; // Hidden comment panels must not capture global shortcuts.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    const typing =
      tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable === true;
    if (e.key === "Escape" && this.frozen) {
      this.unfreeze();
      return;
    }
    if (typing) return; // while the input is focused, Enter submits the form
    // A focused button/select owns its Enter (activation) — hijacking it froze
    // the comment box when e.g. "Pause DAW" or a device select had focus.
    if (target?.closest?.("button, select, a, [role='button']")) return;
    // Enter (or 'c') from anywhere else freezes the heard position + opens the
    // input — so a single Enter sets a marker, a second Enter sends it.
    if (e.key === "Enter" || e.key === "c" || e.key === "C") {
      if (!this.config.enabled || this.frozen) return;
      e.preventDefault();
      this.freezeHere();
    }
  }

  private async submit(): Promise<void> {
    const text = this.inputEl.value.trim();
    if (!text || !this.frozen) return;
    if (!this.config.enabled) {
      this.deps.onError?.("Comments are disabled.");
      return;
    }

    // Post the FROZEN projectSec (docs §4/§7), NOT the live one.
    const projectSec = this.frozen.projectSec;
    const author = this.deps.getAuthor();
    const clientId = this.deps.getClientId();
    const token = this.deps.getToken();

    this.setBusy(true);
    try {
      const { id } = await postComment(
        this.deps.sessionId,
        { text, projectSec, author, clientId },
        token,
      );
      // Optimistic local echo; the relayed `ctrl` copy dedupes by id. Render the
      // owner color immediately from the server-assigned own color (docs §7.1).
      const color = this.deps.getOwnColor() ?? colorForClientId(clientId);
      this.addOrUpdate({
        id,
        projectSec,
        text,
        author,
        ts: Date.now(),
        clientId,
        color,
        edited: false,
      });
      this.unfreeze();
    } catch (e) {
      this.deps.onError?.(`Could not send comment: ${this.msg(e)}`);
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean): void {
    this.inputEl.disabled = busy;
    this.submitBtn.disabled = busy;
    this.cancelBtn.disabled = busy;
    this.submitBtn.textContent = busy ? "\u2026" : "Send";
  }

  // -------------------------------------------------------------------------
  // Edit / delete (docs §7.3)
  // -------------------------------------------------------------------------

  /** Can the current user edit/delete `c`? Owner by clientId, or host (docs §7). */
  private canModerate(c: Comment): boolean {
    if (this.deps.getHostToken()) return true; // host overrides everything
    return !!c.clientId && c.clientId === this.deps.getClientId();
  }

  /** Open the INLINE editor for `c`. Never window.prompt(): a blocking modal
   *  stalls the page's event loop, which starves the audio pipeline (WS frames
   *  queue, iOS suspends the context) — the stream died and stayed dead. */
  private editFlow(c: Comment): void {
    this.editingId = c.id;
    this.editingDraft = c.text;
    this.focusEditorOnRender = true;
    this.render();
  }

  private async saveEdit(c: Comment, text: string): Promise<void> {
    this.editingId = null;
    this.editingDraft = "";
    if (!text || text === c.text) {
      this.render();
      return;
    }
    const hostToken = this.deps.getHostToken();
    // Owner-auth: send clientId + the caller's token. Host uses the streamer JWT;
    // a listener uses their listener token (the server binds the two, docs §7.3).
    const auth = hostToken
      ? { clientId: this.deps.getClientId(), hostToken }
      : { clientId: this.deps.getClientId(), listenerToken: this.deps.getToken() };
    try {
      await editComment(this.deps.sessionId, c.id, text, auth);
      this.applyEdit(c.id, text); // optimistic; ctrl echo dedupes
    } catch (e) {
      this.deps.onError?.(`Edit failed: ${this.msg(e)}`);
      this.render();
    }
  }

  /** Actual delete (armed by the two-tap button — window.confirm() blocked
   *  the event loop and killed the audio exactly like prompt() did). */
  private async deleteFlow(c: Comment): Promise<void> {
    const hostToken = this.deps.getHostToken();
    const auth = hostToken
      ? { clientId: this.deps.getClientId(), hostToken }
      : { clientId: this.deps.getClientId(), listenerToken: this.deps.getToken() };
    try {
      await deleteComment(this.deps.sessionId, c.id, auth);
      this.applyDelete(c.id); // optimistic; ctrl echo also removes
    } catch (e) {
      this.deps.onError?.(`Delete failed: ${this.msg(e)}`);
    }
  }

  // -------------------------------------------------------------------------
  // Config / rendering
  // -------------------------------------------------------------------------

  /** Drop comments that aren't the caller's (entering private mode, docs §7.4). */
  private dropOthersComments(): void {
    const me = this.deps.getClientId();
    for (const [id, c] of this.byId) {
      if (c.clientId && c.clientId !== me) this.byId.delete(id);
    }
  }

  private applyConfigToDom(): void {
    const enabled = this.config.enabled;
    // Host disabled comments (docs §7.4): hide the ENTIRE comment section, not
    // just the input — listeners see no comment UI at all.
    this.rootSection.hidden = !enabled && !this.deps.getHostToken();
    this.disabledHint.hidden = enabled || !!this.deps.getHostToken();
    // Hide the authoring affordances when comments are disabled (docs §7.4).
    if (!enabled) {
      this.freezeBtn.hidden = true;
      this.formEl.hidden = true;
      this.keyHint.hidden = true;
    } else if (!this.frozen) {
      this.freezeBtn.hidden = false;
      this.keyHint.hidden = false;
    }
    this.rootSection.dataset.private = this.config.privateMode ? "1" : "0";
  }

  private render(): void {
    for (const button of this.filterButtons) button.setAttribute("aria-pressed", String(button.dataset.filter === this.filter));
    const items = [...this.byId.values()].filter(c => this.filter === "all" || !this.resolution.get(c.id)?.resolved).sort((a, b) => {
      if (a.projectSec !== b.projectSec) return a.projectSec - b.projectSec;
      return a.ts - b.ts;
    });

    this.listEl.innerHTML = "";
    if (items.length === 0) {
      const empty = document.createElement("li");
      empty.className = "comments__empty";
      empty.textContent = this.config.privateMode
        ? "You did not comment yet."
        : this.byId.size ? "No open comments. Select All to see resolved comments." : "No comments yet.";
      this.listEl.appendChild(empty);
      return;
    }

    for (const c of items) {
      const li = document.createElement("li");
      li.className = "comments__item";
      li.dataset.id = c.id;
      const resolved = this.resolution.get(c.id)?.resolved ?? false;
      li.classList.toggle("comments__item--resolved", resolved);

      // Owner color (docs §7.1/§7.2): server color, else derive from clientId.
      const color = c.color ?? colorForClientId(c.clientId);
      li.style.setProperty("--comment-color", color);

      const tc = document.createElement("button");
      tc.type = "button";
      tc.className = "comments__tc";
      tc.textContent = formatPosition(c.projectSec, this.deps.getCurrentDisp());
      // §13 HOST: the timecode itself is the jump control — one click moves
      // REAPER's edit cursor to the commented moment (no extra button).
      const jumpToken = this.deps.getHostToken();
      if (jumpToken) {
        tc.title = "Jump REAPER to this position";
        tc.classList.add("comments__tc--jump");
        tc.addEventListener("click", () => {
          requestSeek(this.deps.sessionId, c.projectSec, jumpToken).catch((e) =>
            this.deps.onError?.(`Jump failed: ${this.msg(e)}`),
          );
        });
      } else {
        tc.title = "Project position";
      }

      const body = document.createElement("div");
      body.className = "comments__body";

      const meta = document.createElement("div");
      meta.className = "comments__meta";

      const author = document.createElement("span");
      author.className = "comments__author";
      author.textContent = c.author || "Guest";
      author.style.color = color;
      meta.appendChild(author);

      if (c.edited) {
        const edited = document.createElement("span");
        edited.className = "comments__edited";
        edited.textContent = "(edited)";
        edited.title = "This comment was edited";
        meta.appendChild(edited);
      }

      if (this.editingId === c.id) {
        // INLINE editor (never a blocking prompt): textarea + Save/Cancel.
        // Draft survives re-renders (live comments keep arriving underneath).
        const editor = document.createElement("textarea");
        editor.className = "comments__input comments__editinput";
        editor.maxLength = 500;
        editor.rows = 1;
        editor.value = this.editingDraft;
        editor.addEventListener("input", () => {
          this.editingDraft = editor.value;
          this.autogrow(editor);
        });
        editor.addEventListener("keydown", (e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void this.saveEdit(c, editor.value.trim());
          } else if (e.key === "Escape") {
            this.editingId = null;
            this.editingDraft = "";
            this.render();
          }
        });

        const editActions = document.createElement("div");
        editActions.className = "comments__formactions";
        const saveBtn = document.createElement("button");
        saveBtn.type = "button";
        saveBtn.className = "comments__submit";
        saveBtn.textContent = "Save";
        saveBtn.addEventListener("click", () => void this.saveEdit(c, editor.value.trim()));
        const cancelBtn = document.createElement("button");
        cancelBtn.type = "button";
        cancelBtn.className = "btn btn--ghost comments__cancel";
        cancelBtn.textContent = "Cancel";
        cancelBtn.addEventListener("click", () => {
          this.editingId = null;
          this.editingDraft = "";
          this.render();
        });
        editActions.append(saveBtn, cancelBtn);

        body.append(meta, editor, editActions);
        li.append(tc, body);
        this.listEl.appendChild(li);
        // Size the textarea to its content once it is in the document.
        this.autogrow(editor);
        if (this.focusEditorOnRender) {
          this.focusEditorOnRender = false;
          editor.focus();
        }
        continue;
      }

      const textEl = document.createElement("span");
      textEl.className = "comments__text";
      textEl.textContent = c.text; // textContent => no HTML injection

      if (resolved) {
        const badge = document.createElement("span"); badge.className = "comments__resolved"; badge.textContent = "Resolved"; meta.append(badge);
      }
      body.append(meta, textEl);
      li.append(tc, body);

      // Edit/delete on the user's own comments (or all in HOST mode, docs §7.3).
      if (this.canModerate(c)) {
        const actions = document.createElement("div");
        actions.className = "comments__actions";

        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "comments__action";
        editBtn.textContent = "Edit";
        editBtn.addEventListener("click", () => this.editFlow(c));

        // Two-tap delete: first tap ARMS ("Sure?", 3 s), second tap deletes.
        const delBtn = document.createElement("button");
        delBtn.type = "button";
        delBtn.className = "comments__action comments__action--danger";
        delBtn.textContent = "Delete";
        let armTimer = 0;
        delBtn.addEventListener("click", () => {
          if (delBtn.dataset.armed === "1") {
            window.clearTimeout(armTimer);
            void this.deleteFlow(c);
            return;
          }
          delBtn.dataset.armed = "1";
          delBtn.textContent = "Sure?";
          armTimer = window.setTimeout(() => {
            delete delBtn.dataset.armed;
            delBtn.textContent = "Delete";
          }, 3000);
        });

        if (this.deps.getHostToken()) {
          const resolve = document.createElement("button"); resolve.type = "button"; resolve.className = "comments__action comments__resolve";
          resolve.textContent = resolved ? "Reopen" : "Resolve"; resolve.disabled = this.resolving.has(c.id);
          resolve.setAttribute("aria-label", `${resolved ? "Reopen" : "Resolve"} comment by ${c.author}`);
          resolve.onclick = () => { void this.toggleResolved(c); }; actions.append(resolve);
        }
        actions.append(editBtn, delBtn);
        li.appendChild(actions);
      }

      this.listEl.appendChild(li);
    }
  }

  private msg(e: unknown): string {
    if (e instanceof ApiError) return e.message;
    if (e instanceof Error) return e.message;
    return String(e);
  }

  destroy(): void {
    if (this.posTimer !== null) {
      window.clearInterval(this.posTimer);
      this.posTimer = null;
    }
    window.removeEventListener("keydown", this.onKeyDown);
  }
}
